export const FEDEX_SHIPMENT_COLUMN_KEYS = [
  'tracking',
  'shipDate',
  'service',
  'sla',
  'packageType',
  'brand',
  'zone',
  'transportation',
  'discount',
  'realCost',
  'charged',
  'margin',
  'flags',
] as const;

export type FedexShipmentColumnKey = (typeof FEDEX_SHIPMENT_COLUMN_KEYS)[number];

export const DEFAULT_FEDEX_SHIPMENT_COLUMN_KEYS: readonly FedexShipmentColumnKey[] = [
  'tracking',
  'shipDate',
  'service',
  'sla',
  'packageType',
  'brand',
  'realCost',
  'charged',
  'margin',
  'flags',
];

export const FEDEX_SHIPMENT_COLUMN_PREFERENCES_STORAGE_KEY = 'fedex:shipment-columns';

const PREFERENCES_VERSION = 2;
const COLUMN_KEY_SET = new Set<string>(FEDEX_SHIPMENT_COLUMN_KEYS);

function defaults(): FedexShipmentColumnKey[] {
  return [...DEFAULT_FEDEX_SHIPMENT_COLUMN_KEYS];
}

function normalizeVisibleKeys(value: unknown): FedexShipmentColumnKey[] | null {
  if (!Array.isArray(value)) return null;

  const selected = new Set<FedexShipmentColumnKey>();
  for (const candidate of value) {
    if (typeof candidate === 'string' && COLUMN_KEY_SET.has(candidate)) {
      selected.add(candidate as FedexShipmentColumnKey);
    }
  }
  if (selected.size === 0) return null;

  return FEDEX_SHIPMENT_COLUMN_KEYS.filter((key) => selected.has(key));
}

export function serializeFedexShipmentColumnPreferences(
  visibleKeys: readonly FedexShipmentColumnKey[],
): string {
  return JSON.stringify({
    version: PREFERENCES_VERSION,
    visible: normalizeVisibleKeys(visibleKeys) ?? defaults(),
  });
}

export function parseFedexShipmentColumnPreferences(
  storedValue: string | null | undefined,
): FedexShipmentColumnKey[] {
  if (typeof storedValue !== 'string' || storedValue.trim() === '') return defaults();

  try {
    const parsed: unknown = JSON.parse(storedValue);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults();

    const preferences = parsed as { version?: unknown; visible?: unknown };
    if (preferences.version === 1) {
      const prior = normalizeVisibleKeys(preferences.visible);
      if (prior === null) return defaults();
      const migrated = new Set<FedexShipmentColumnKey>([...prior, 'sla']);
      return FEDEX_SHIPMENT_COLUMN_KEYS.filter((key) => migrated.has(key));
    }
    if (preferences.version !== PREFERENCES_VERSION) return defaults();
    return normalizeVisibleKeys(preferences.visible) ?? defaults();
  } catch {
    return defaults();
  }
}
